import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./Header.css";
import { logout } from "../../../redux/actions/authActions";
import Button from "@mui/material/Button";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { fetchSiteConfig } from "../../../redux/actions/dataActions";
import { API_URL } from "../../../redux/actions/api";
import { getTenant } from "../../../config/tenants";
import { useT } from "../../../i18n";
import { useLogoutIconProps } from "../../../i18n/DirectionalIcons";

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useT();
  // ★ שומר על האייקון בצד השמאלי של הטקסט בכל שפה (ראה DirectionalIcons).
  const logoutIconProps = useLogoutIconProps(<LogoutRoundedIcon />);
  const { isAuthenticated } = useSelector((state) => state.auth);

  // Enlarge the logo only on the genuine login screen. On an F5 reload Redux boots
  // with isAuthenticated=false until the token is re-validated, which would flash
  // the big logo for a frame before it shrinks. A token in localStorage means the
  // user is (or is about to be) authenticated, so skip the enlarge right away.
  // LOGOUT removes the token, so it correctly re-enlarges on the login screen.
  const hasToken =
    typeof window !== "undefined" && !!localStorage.getItem("token");
  const showLoginLogo = !isAuthenticated && !hasToken;

  const logoutFunc = () => {
    dispatch(logout());
    window.location.replace("/");
  };

  // Per-tenant logo + sizing comes from the single config source. The backend
  // site-config (below) overrides the logo at runtime when it returns one.
  const tenant = getTenant();

  const resolveRelativeLogoUrl = (value) => {
    if (typeof value !== "string" || /^https?:\/\//i.test(value)) {
      return value;
    }
    const host = tenant.logoHost || new URL(API_URL).origin;
    return `${host}/${value.replace(/^\/+/, "")}`;
  };

  // Seed from the last-known logo (localStorage) so a refresh shows the correct logo
  // immediately instead of flashing the bundled/previous one until the async
  // site-config fetch returns. Re-saved after each successful fetch (below).
  const cachedLogo = (() => {
    try { return JSON.parse(localStorage.getItem("ans_site_logo_v1") || "null"); }
    catch (e) { return null; }
  })();
  const [cfgLogo, setCfgLogo] = useState(cachedLogo?.logo || null);
  const [cfgHeight, setCfgHeight] = useState(cachedLogo?.height || null);
  const [cfgMarginTop, setCfgMarginTop] = useState(cachedLogo?.marginTop || null);
  // On a COLD load (no cached logo) we don't yet know which logo the admin picked in
  // site-settings, so instead of flashing the bundled tenant fallback (e.g. a previous
  // client's logo) we reserve the space and fade the real logo in once site-config
  // returns. A warm cache means we already know it → render immediately, no placeholder.
  const [cfgLoaded, setCfgLoaded] = useState(Boolean(cachedLogo));

  const normalizePx = (val) => {
    if (!val) return val;
    val = String(val).trim();
    return /^\d+$/.test(val) ? val + "px" : val;
  };

  useEffect(() => {
    fetchSiteConfig()(dispatch).then((config) => {
      const cache = {};
      if (config.site_logo) {
        const l = resolveRelativeLogoUrl(config.site_logo);
        setCfgLogo(l); cache.logo = l;
      }
      if (config.logo_height) {
        const h = normalizePx(config.logo_height);
        setCfgHeight(h); cache.height = h;
      }
      if (config.logo_margin_top) {
        const m = normalizePx(config.logo_margin_top);
        setCfgMarginTop(m); cache.marginTop = m;
      }
      try { localStorage.setItem("ans_site_logo_v1", JSON.stringify(cache)); }
      catch (e) { /* ignore quota / private mode */ }
      setCfgLoaded(true);   // config resolved → reveal (real logo, or the tenant fallback if none)
    }).catch(() => setCfgLoaded(true));   // even on failure, stop reserving and show the fallback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const useSiteSettingsLogo = Boolean(cfgLogo);
  const logo = useSiteSettingsLogo ? cfgLogo : tenant.logo;
  const logo_height = useSiteSettingsLogo ? cfgHeight ?? tenant.logoHeight : tenant.logoHeight;
  const logo_margin_top = useSiteSettingsLogo
    ? cfgMarginTop ?? tenant.logoMarginTop
    : tenant.logoMarginTop;

  return (
    <section className={`header is-sticky${!isAuthenticated ? " header--login" : ""}`}>
      {!cfgLoaded ? (
        /* cold load: reserve the space, don't flash a (possibly wrong) bundled logo */
        <div
          style={{ height: logo_height, marginTop: logo_margin_top }}
          className={`logo logo--placeholder${showLoginLogo ? " logo--login" : ""}`}
          aria-hidden="true"
        />
      ) : logo ? (
        <img
          src={logo}
          style={{ height: logo_height, marginTop: logo_margin_top }}
          alt={t("common.logoAlt")}
          className={`logo logo--reveal${showLoginLogo ? " logo--login" : ""}`}
          onClick={() => navigate("/")}
          /* a11y (WCAG 2.1.1): the logo doubles as a "home" link → make it keyboard-operable */
          role="button"
          tabIndex={0}
          aria-label={t("common.goHome")}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate("/"); } }}
        />
      ) : (
        <div
          style={{ height: logo_height, marginTop: logo_margin_top }}
          className={`logo${showLoginLogo ? " logo--login" : ""}`}
        />
      )}

      <div className="side-header">
        <div className="side-header-items">
          {isAuthenticated && (
            <Button
              onClick={logoutFunc}
              size="small"
              className="logout-btn"
              {...logoutIconProps}
              aria-label={t("header.logout")}
            >
              {t("header.logout")}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

export default Header;
